import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  DollarSign, 
  Briefcase, 
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  Database
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const MetricCard = ({ title, value, change, changeType, icon: Icon, subtitle }) => (
  <Card className="metric-card" data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">{title}</p>
          <p className="font-heading text-3xl font-light text-slate-900 mt-2">{value}</p>
          {subtitle && (
            <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
          )}
          {change !== undefined && (
            <div className={`flex items-center mt-2 text-sm ${changeType === 'positive' ? 'text-emerald-600' : 'text-red-600'}`}>
              {changeType === 'positive' ? (
                <ArrowUpRight className="h-4 w-4 mr-1" />
              ) : (
                <ArrowDownRight className="h-4 w-4 mr-1" />
              )}
              <span>{change}</span>
            </div>
          )}
        </div>
        <div className="p-3 bg-slate-100 rounded-lg">
          <Icon className="h-5 w-5 text-[#1E3A5F]" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function DashboardPage() {
  const { getAuthHeaders, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, performanceRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/overview`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/analytics/performance`, { headers: getAuthHeaders() })
      ]);
      setAnalytics(analyticsRes.data);
      setChartData(performanceRes.data.chart_data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API_URL}/api/seed`, {}, { headers: getAuthHeaders() });
      toast.success('Sample data added successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  const unrealizedGainPercent = analytics?.total_invested > 0 
    ? ((analytics.unrealized_gain / analytics.total_invested) * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-500 mt-1">Here's your portfolio overview</p>
        </div>
        {user?.role === 'admin' && analytics?.total_investments === 0 && (
          <Button 
            onClick={handleSeedData} 
            disabled={seeding}
            className="btn-primary"
            data-testid="seed-data-btn"
          >
            {seeding ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Database className="h-4 w-4 mr-2" />
            )}
            Add Sample Data
          </Button>
        )}
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total AUM"
          value={formatCurrency(analytics?.total_current_value || 0)}
          change={`${unrealizedGainPercent}% total return`}
          changeType={analytics?.unrealized_gain >= 0 ? 'positive' : 'negative'}
          icon={DollarSign}
        />
        <MetricCard
          title="Total Invested"
          value={formatCurrency(analytics?.total_invested || 0)}
          subtitle={`${analytics?.total_investments || 0} investments`}
          icon={Briefcase}
        />
        <MetricCard
          title="Portfolio MOIC"
          value={`${analytics?.portfolio_moic || 0}x`}
          subtitle="Multiple on invested capital"
          icon={TrendingUp}
        />
        <MetricCard
          title="Active Deals"
          value={analytics?.active_deals || 0}
          subtitle="In pipeline"
          icon={Target}
        />
      </div>

      {/* Chart Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" data-testid="portfolio-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Portfolio Growth</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1E3A5F" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#1E3A5F" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorInvested" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#38BDF8" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#38BDF8" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#94a3b8"
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="#94a3b8"
                    fontSize={12}
                    tickLine={false}
                    tickFormatter={(value) => formatCurrency(value)}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      borderRadius: '8px', 
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    name="Current Value"
                    stroke="#1E3A5F" 
                    fillOpacity={1} 
                    fill="url(#colorValue)" 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="invested" 
                    name="Invested"
                    stroke="#38BDF8" 
                    fillOpacity={1} 
                    fill="url(#colorInvested)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-slate-400">
                <p>No investment data yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sectors Breakdown */}
        <Card data-testid="sectors-breakdown">
          <CardHeader>
            <CardTitle className="font-heading text-lg">By Sector</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.sectors && Object.keys(analytics.sectors).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(analytics.sectors).map(([sector, count]) => (
                  <div key={sector} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-[#1E3A5F]" />
                      <span className="text-sm text-slate-700">{sector}</span>
                    </div>
                    <Badge variant="secondary" className="font-mono">
                      {count}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-slate-400">
                <p>No data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Stats */}
      {analytics?.pipeline_stats && Object.keys(analytics.pipeline_stats).length > 0 && (
        <Card data-testid="pipeline-stats">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Pipeline Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(analytics.pipeline_stats).map(([stage, count]) => (
                <div 
                  key={stage} 
                  className={`stage-badge stage-${stage.replace('_', '-')}`}
                >
                  {stage.replace('_', ' ')} ({count})
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
